import * as Sentry from '@sentry/react';

const LANDING_PAGE_KEY = 'landing_page';

const STORED_KEYS = [
    'gclid',
    'gbraid',
    'wbraid',
    'fbclid',
    'li_fat_id',
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
    LANDING_PAGE_KEY,
] as const;

const META_PIXEL_KEYS = ['fbc', 'fbp'] as const;

type StoredKey = (typeof STORED_KEYS)[number];
type MetaPixelKey = (typeof META_PIXEL_KEYS)[number];
type AttributionKey = StoredKey | MetaPixelKey;

export type Attribution = Partial<Record<AttributionKey, string>>;

const VALUE_MAX_LENGTHS: Record<AttributionKey, number> = {
    gclid: 100,
    gbraid: 250,
    wbraid: 250,
    fbclid: 500,
    li_fat_id: 250,
    utm_source: 100,
    utm_medium: 100,
    utm_campaign: 100,
    utm_term: 250,
    utm_content: 500,
    [LANDING_PAGE_KEY]: 500,
    fbc: 600,
    fbp: 100,
};

const META_PIXEL_COOKIE_NAMES: Record<MetaPixelKey, string> = {
    fbc: '_fbc',
    fbp: '_fbp',
};

const COOKIE_NAME = 'hg_attribution';
const MAX_AGE_SECONDS = 90 * 24 * 60 * 60;
const COOKIE_VALUE_MAX_LENGTH = 3500;
const FIRST_PRINTABLE_CHAR_CODE = 32;
const DELETE_CHAR_CODE = 127;
const LAST_C1_CONTROL_CHAR_CODE = 159;
const SHARED_DOMAIN = 'joinhomegrown.com';

const hasControlCharacters = (value: string) =>
    [...value].some((char) => {
        const code = char.charCodeAt(0);
        return (
            code < FIRST_PRINTABLE_CHAR_CODE ||
            (code >= DELETE_CHAR_CODE && code <= LAST_C1_CONTROL_CHAR_CODE)
        );
    });

const pickAttribution = <Key extends AttributionKey>(
    keys: readonly Key[],
    readValue: (key: Key) => unknown
): Attribution => {
    const attribution: Attribution = {};
    for (const key of keys) {
        const value = readValue(key);
        if (typeof value !== 'string') continue;

        const trimmed = value.trim();
        if (!trimmed || hasControlCharacters(trimmed)) continue;
        if (trimmed.length > VALUE_MAX_LENGTHS[key]) {
            Sentry.captureMessage('Attribution value over the length limit dropped', {
                level: 'warning',
                tags: { key, length: trimmed.length },
            });
            continue;
        }
        attribution[key] = trimmed;
    }
    return attribution;
};

const hasTrackedParameter = (attribution: Attribution) =>
    Object.keys(attribution).some((key) => key !== LANDING_PAGE_KEY);

const readCookie = (name: string) => {
    const prefix = `${name}=`;
    return document.cookie
        .split('; ')
        .find((entry) => entry.startsWith(prefix))
        ?.slice(prefix.length);
};

const cookieAttributes = () => {
    const { hostname, protocol } = window.location;
    const attributes = ['path=/', `max-age=${MAX_AGE_SECONDS}`, 'SameSite=Lax'];
    if (protocol === 'https:') attributes.push('Secure');
    if (hostname === SHARED_DOMAIN || hostname.endsWith(`.${SHARED_DOMAIN}`)) {
        attributes.push(`domain=.${SHARED_DOMAIN}`);
    }
    return attributes.join('; ');
};

export const captureAttribution = () => {
    const { search, origin, pathname } = window.location;
    const params = new URLSearchParams(search);
    const attribution = pickAttribution(STORED_KEYS, (key) =>
        key === LANDING_PAGE_KEY ? `${origin}${pathname}` : params.get(key)
    );
    if (!hasTrackedParameter(attribution)) return;

    const value = encodeURIComponent(JSON.stringify(attribution));
    if (value.length > COOKIE_VALUE_MAX_LENGTH) {
        Sentry.captureMessage('Attribution set over the cookie limit dropped', {
            level: 'warning',
            tags: { length: value.length },
        });
        return;
    }

    document.cookie = `${COOKIE_NAME}=${value}; ${cookieAttributes()}`;
};

const readStoredAttribution = (): Attribution => {
    const cookie = readCookie(COOKIE_NAME);
    if (!cookie) return {};

    let parsed: unknown;
    try {
        parsed = JSON.parse(decodeURIComponent(cookie));
    } catch {
        return {};
    }
    if (!parsed || typeof parsed !== 'object') return {};

    const stored = parsed as Record<string, unknown>;
    return pickAttribution(STORED_KEYS, (key) => stored[key]);
};

export const readAttribution = (): Attribution | undefined => {
    const stored = readStoredAttribution();
    if (!hasTrackedParameter(stored)) return undefined;

    return {
        ...stored,
        ...pickAttribution(META_PIXEL_KEYS, (key) => readCookie(META_PIXEL_COOKIE_NAMES[key])),
    };
};
